import { useEffect, useRef } from 'react';
import Hls from 'hls.js';

/**
 * HLS形式の動画再生を管理するカスタムフック（.segment拡張子対応）
 * @param {string} src - 動画のURL（.m3u8ファイルのパス）
 * @param {Object} videoRef - video要素のref
 * @param {boolean} enabled - HLS再生を有効にするかどうか
 * @returns {Object} - HLSインスタンスとサポート状況
 */

// .segment拡張子を使用するかどうか（false の場合は標準的な.ts拡張子を使用）
const USE_SEGMENT_EXTENSION = true;

// ネットワークエラー時の startLoad() 再試行の上限。
// マニフェストが 403/404 を返す場合は何度試しても復旧しないため、
// 上限を設けないと 1 秒間隔でリクエストを撃ち続けることになる
const MAX_NETWORK_RETRIES = 3;
const NETWORK_RETRY_DELAY_MS = 1000;

/**
 * URL が HLS プレイリストを指すかどうか。
 * 署名付き URL などクエリが付く場合があるため、必ずクエリを除いてから判定する。
 * @param {string|null|undefined} src - 判定対象の URL
 * @returns {boolean}
 */
export const isHlsSource = (src) =>
  typeof src === 'string' && src.split('?')[0].toLowerCase().endsWith('.m3u8');

export const useHlsPlayer = (src, videoRef, enabled = true) => {
  const hlsRef = useRef(null);

  useEffect(() => {
    if (!enabled || !src || !videoRef.current) return;

    const video = videoRef.current;
    let networkRetries = 0;
    let retryTimer = null;

    // HLSがサポートされているかチェック
    // 注意: 判定は必ず Hls.isSupported()（MSE の有無）を先に行うこと。
    // Chrome 142+ はネイティブ HLS デマクサを搭載し
    // canPlayType('application/vnd.apple.mpegurl') が "maybe" を返すが、
    // MediaConvert 出力の EXT-X-BYTERANGE 付きプレイリストを再生できず
    // NotSupportedError になる（hls.js issue #7827 と同一事象）。
    // MSE を持つブラウザ（Chrome / Edge / Firefox / macOS Safari）は常に
    // hls.js を使い、ネイティブ HLS は MSE の無い iOS Safari 等のみに限定する。
    if (Hls.isSupported()) {
      // HLS.jsを使用
      const hls = new Hls({
        // パフォーマンス最適化設定
        enableWorker: true,
        lowLatencyMode: false,
        maxBufferLength: 30,
        maxMaxBufferLength: 600,
        maxBufferSize: 60 * 1000 * 1000, // 60 MB
        maxBufferHole: 0.5,
        // ネットワーク設定
        manifestLoadingTimeOut: 10000,
        manifestLoadingMaxRetry: 3,
        levelLoadingTimeOut: 10000,
        levelLoadingMaxRetry: 4,
        // 品質設定
        startLevel: -1, // 自動品質選択
        autoStartLoad: true,
        // 高解像度動画のための追加設定
        capLevelToPlayerSize: true, // プレイヤーサイズに応じて品質を制限
        maxLoadingDelay: 4, // 最大読み込み遅延
        highBufferWatchdogPeriod: 2, // 高バッファ監視期間
        // デバッグ（本番環境では false に設定）
        debug: import.meta.env.DEV,
        // カスタムローダー設定（.segment拡張子を使用する場合のみ）
        ...(USE_SEGMENT_EXTENSION && {
          loader: class CustomLoader extends Hls.DefaultConfig.loader {
            constructor(config) {
              super(config);
              // .segmentファイルをビデオセグメントとして扱う
              const load = this.load.bind(this);
              this.load = function(context, config, callbacks) {
                // URLが.segmentで終わる場合、コンテキストタイプを設定
                if (context.url && context.url.endsWith('.segment')) {
                  context.responseType = 'arraybuffer';
                }
                return load(context, config, callbacks);
              };
            }
          },
        }),
      });

      hlsRef.current = hls;

      // HLSインスタンスをビデオ要素にアタッチ
      hls.attachMedia(video);

      // マニフェストが読み込まれたらソースをロード
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        if (import.meta.env.DEV) {
          console.log('HLS: Media attached, loading source:', src);
        }
        hls.loadSource(src);
      });

      // マニフェストの読み込み成功
      hls.on(Hls.Events.MANIFEST_LOADED, (event, data) => {
        if (import.meta.env.DEV) {
          console.log('HLS: Manifest loaded successfully', {
            levels: data.levels.length,
            audioTracks: data.audioTracks.length,
            url: data.url,
          });
        }
      });

      // フラグメントの読み込み開始
      hls.on(Hls.Events.FRAG_LOADING, (event, data) => {
        if (import.meta.env.DEV) {
          console.log('HLS: Loading fragment', {
            sn: data.frag.sn,
            url: data.frag.url,
          });
        }
      });

      // エラーハンドリング
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS Error:', data.type, data.details, data);
        
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('ネットワークエラーが発生しました', data);
              if (networkRetries >= MAX_NETWORK_RETRIES) {
                console.error(`HLS のネットワークエラーが${MAX_NETWORK_RETRIES}回続いたため再試行を中止します`, data.url || src);
                hls.destroy();
                break;
              }
              networkRetries += 1;
              retryTimer = setTimeout(() => {
                hls.startLoad();
              }, NETWORK_RETRY_DELAY_MS);
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error('メディアエラーが発生しました', data);
              // メディアエラーの詳細をログ
              console.error('エラー詳細:', {
                details: data.details,
                level: data.level,
                fatal: data.fatal,
                url: data.url || data.frag?.url,
              });
              // メディアエラーからの回復を試みる
              hls.recoverMediaError();
              // それでも失敗した場合は、低品質に切り替える
              if (hls.currentLevel > 0) {
                console.log('低品質レベルに切り替えます');
                hls.currentLevel = 0;
              }
              break;
            default:
              console.error('再生エラーが発生しました', data);
              hls.destroy();
              break;
          }
        } else {
          // 致命的でないエラーもログに記録
          console.warn('非致命的なHLSエラー:', data);
        }
      });

      // レベル切り替えイベント（品質変更時）
      hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
        if (import.meta.env.DEV) {
          console.log(`品質レベルが ${data.level} に切り替わりました`);
        }
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // MSE の無い iOS Safari 等のネイティブHLSサポート
      video.src = src;
    } else {
      console.error('このブラウザはHLS再生をサポートしていません');
    }

    // クリーンアップ
    return () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, enabled]);

  return {
    hlsInstance: hlsRef.current,
    // 遅延参照用（レンダリング時点では未生成のことがあるため ref 自体も公開する）
    hlsRef,
    isSupported: Hls.isSupported() || videoRef.current?.canPlayType('application/vnd.apple.mpegurl'),
  };
};
